import {
  forwardRef,
  memo,
  useCallback,
  useMemo,
  useLayoutEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useBotContext } from '../useBotContext';
import AccessoryList from './accessory_list';
import AddAccessoryButtons from './add_accessory';
import { accessoryTypes } from '../../helpers/data/bot';
import { BotAccessoriesPanelProps, IBotData, ILabelValue, ACCESSORIES_PANEL_OFFSETS } from '../../models';
import type { IState } from '../../../../models';
import './styles.scss';

const BotAccessoriesPanel = forwardRef<
  HTMLDivElement,
  BotAccessoriesPanelProps
>(
  (
    {
      selected,
      section,
      tunnelIndex,
      sectionIndex,
      isOpen = false,
      position = null,
    },
    ref
  ) => {
    const { setValue, value: botData } = useBotContext();
    const accessories = useSelector((state: IState) => state.ahu.accessories);

    const [calculatedTop, setCalculatedTop] = useState<number | null>(null);

    // Check if this is the last section in the tunnel
    const isLastSection = useMemo(() => {
      if (tunnelIndex === null || sectionIndex === null || !botData?.tunnels) {
        return false;
      }
      const tunnel = botData.tunnels[tunnelIndex];
      if (!tunnel || !tunnel.sections) return false;
      return sectionIndex === tunnel.sections.length - 1;
    }, [tunnelIndex, sectionIndex, botData?.tunnels]);

    const options = useMemo(() => {
      const availableTypes = accessoryTypes.filter((option) =>
        accessories.some((stored) => stored.value === option.value)
      );

      // Filter out drain_pan unless section.type_id === 1 and section.type === 'coil'
      const filteredTypes = availableTypes.filter((option) => {
        if (option.value === 'drain_pan') {
          return section?.type === 'coil' && section?.type_id === 1;
        }
        return true;
      });

      if (!section?.accessories?.length) {
        return filteredTypes;
      }

      const usedTypes = new Set(section.accessories.map((acc) => acc.type));
      return filteredTypes.filter((option) => !usedTypes.has(option.value));
    }, [accessories, section?.accessories, section?.type, section?.type_id]);

    const addAccessory = useCallback(
      (accessory: ILabelValue<string>) => {
        if (tunnelIndex === null || sectionIndex === null) return;

        setValue((draft: IBotData) => {
          const target = draft.tunnels[tunnelIndex]?.sections[sectionIndex];
          if (!target) return;

          target.accessories ||= [];
          const newAccessory: any = {
            type: accessory.value,
            mountLocation: 'left',
            name: accessory.label,
          };
          if (accessory.value === 'drain_pan') {
            newAccessory.conn_hand_id = 2;
          }
          if (accessory.value === 'opening') {
            newAccessory.mounting_plane_id = 3;
          }
          if (accessory.value === 'access_door') {
            newAccessory.mounting_plane_id = 6;
          }
          target.accessories.push(newAccessory);
        });
      },
      [tunnelIndex, sectionIndex, setValue]
    );

    const removeAccessory = useCallback(
      (idx: number) => {
        /**
         * Removes an accessory from the section with business rule validation
         * 
         * Prevents removal of critical accessories:
         * - drain_pan: Cannot remove from coil sections with type_id === 1
         *   (required for liquid collection in cooling coil sections)
         * - opening: Cannot remove from last section in tunnel
         *   (ensures tunnel remains open for air flow)
         * - access_door: Cannot remove from fan/plenum/coil(duty_id=1) sections
         *   (required for component access and maintenance)
         */
        if (tunnelIndex === null || sectionIndex === null) return;

        setValue((draft: IBotData) => {
          const target = draft.tunnels[tunnelIndex]?.sections[sectionIndex];
          if (!target) return;

          const accessory = target.accessories?.[idx];
          if (!accessory) return;

          // Prevent removal of opening accessory on last section
          if (isLastSection && accessory.type === 'opening') {
            return;
          }

          // Prevent removal of drain pan accessory when type_id === 1 for coil sections
          if (
            target.type === 'coil' &&
            target.type_id === 1 &&
            accessory.type === 'drain_pan'
          ) {
            return;
          }

          // Prevent removal of access_door accessory for fan, plenum, and coil (duty_id=1) sections
          // Based on createAccessDoorIfNeeded logic
          const needsAccessDoor =
            target.type === 'fan' ||
            target.type === 'plenum' ||
            (target.type === 'coil' && target.duty_id === 1);
          if (needsAccessDoor && accessory.type === 'access_door') {
            return;
          }

          target.accessories.splice(idx, 1);
        });
      },
      [tunnelIndex, sectionIndex, setValue, isLastSection]
    );

    const updateMountLocation = useCallback(
      (idx: number) => {
        if (tunnelIndex === null || sectionIndex === null) return;

        setValue((draft: IBotData) => {
          const acc =
            draft.tunnels[tunnelIndex]?.sections[sectionIndex]?.accessories[
              idx
            ];

          if (!acc) return;
        });
      },
      [tunnelIndex, sectionIndex, setValue]
    );

    const updateSectionField = useCallback(
      (value: string | number | null, name: string) => {
        if (tunnelIndex === null || sectionIndex === null) return;

        setValue((draft: IBotData) => {
          const target = draft.tunnels?.[tunnelIndex]?.sections?.[sectionIndex];
          if (!target) return;
          const parsed =
            value === null
              ? undefined
              : typeof value === 'string' && !isNaN(Number(value))
              ? Number(value)
              : value;
          (target as any)[name] = parsed;
        });
      },
      [tunnelIndex, sectionIndex, setValue]
    );

    const updateAccessoryField = useCallback(
      (idx: number, value: string | number | null, name: string) => {
        if (tunnelIndex === null || sectionIndex === null) return;

        setValue((draft: IBotData) => {
          const target = draft.tunnels[tunnelIndex]?.sections[sectionIndex];
          if (!target || !target.accessories || !target.accessories[idx]) return;
          
          const parsed =
            value === null
              ? undefined
              : typeof value === 'string' && !isNaN(Number(value))
              ? Number(value)
              : value;
          (target.accessories[idx] as any)[name] = parsed;
        });
      },
      [tunnelIndex, sectionIndex, setValue]
    );

    useLayoutEffect(() => {
      if (
        !position ||
        !ref ||
        !(ref as React.RefObject<HTMLDivElement>).current
      ) {
        setCalculatedTop(null);
        return;
      }
      const el = (ref as React.RefObject<HTMLDivElement>).current;
      const panelHeight = el?.offsetHeight || 0;

      const baseTop = position.top + ACCESSORIES_PANEL_OFFSETS.BASE_TOP_OFFSET;

      const nextTop =
        baseTop + panelHeight - ACCESSORIES_PANEL_OFFSETS.BASE_TOP_OFFSET > position.height
          ? baseTop - panelHeight - ACCESSORIES_PANEL_OFFSETS.BUFFER_OFFSET
          : baseTop;

      setCalculatedTop(nextTop);
    }, [position, ref, section?.accessories?.length]);

    if (!selected || !section || !section.type || !isOpen) return null;

    const style: React.CSSProperties =
      position && calculatedTop !== null
        ? {
            top: `${calculatedTop}px`,
          }
        : {};

    return (
      <div ref={ref} className="bot-accessories" style={style}>
        <div className="bot-accessories__title">Section Accessories</div>

        <AddAccessoryButtons onAdd={addAccessory} options={options} />

        <AccessoryList
          accessories={section.accessories || []}
          onRemove={removeAccessory}
          onLocationChange={updateMountLocation}
          isLastSection={isLastSection}
          section={section}
          onSectionFieldChange={updateSectionField}
          onAccessoryFieldChange={updateAccessoryField}
          tunnelIndex={tunnelIndex}
          airflow_direction_id={botData.tunnels[tunnelIndex].airflow_direction_id}
          tunnel_configuration={botData.tunnel_configuration}
        />
      </div>
    );
  }
);

BotAccessoriesPanel.displayName = 'BotAccessoriesPanel';

export default memo(BotAccessoriesPanel);
